/**
 * EnergyMonthlyChart Component
 *
 * Stacked bar chart showing monthly energy costs (energy + standing charge)
 * over 24 months, with values including VAT.
 */

import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts'

function formatCurrency(value) {
  if (value >= 1000) return `£${(value / 1000).toFixed(1)}k`
  return `£${value.toFixed(0)}`
}

function formatMonth(month) {
  const date = new Date(month)
  return date.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' })
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null

  const date = new Date(label)
  const monthYear = date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })

  // Calculate total
  const total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0)

  return (
    <div className="bg-white border border-neutral-200 rounded-lg shadow-lg p-3">
      <p className="font-medium text-neutral-900 mb-2">{monthYear}</p>
      {payload.map((entry, index) => (
        <div key={index} className="flex items-center justify-between gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-neutral-600">{entry.name}</span>
          </div>
          <span className="font-medium text-neutral-900">
            {new Intl.NumberFormat('en-GB', {
              style: 'currency', currency: 'GBP',
              minimumFractionDigits: 0, maximumFractionDigits: 0
            }).format(entry.value)}
          </span>
        </div>
      ))}
      <div className="border-t border-neutral-200 mt-2 pt-2 flex items-center justify-between text-sm">
        <span className="font-medium text-neutral-600">Total</span>
        <span className="font-bold text-neutral-900">
          {new Intl.NumberFormat('en-GB', {
            style: 'currency', currency: 'GBP',
            minimumFractionDigits: 0, maximumFractionDigits: 0
          }).format(total)}
        </span>
      </div>
    </div>
  )
}

export default function EnergyMonthlyChart({ monthly, vatRate = 0.20, isLoading }) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Monthly Energy Costs</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[320px] w-full" />
        </CardContent>
      </Card>
    )
  }

  if (!monthly?.length) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Monthly Energy Costs</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-[320px] text-neutral-500">
            No data available
          </div>
        </CardContent>
      </Card>
    )
  }

  const chartData = monthly.map(m => ({
    month: m.month,
    energy: parseFloat(m.cost_excl_vat_pence) / 100 * (1 + vatRate),
    standing: parseFloat(m.standing_excl_vat_pence) / 100 * (1 + vatRate)
  }))

  return (
    <Card>
      <CardHeader>
        <CardTitle>Monthly Energy Costs</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="month"
              tickFormatter={formatMonth}
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#6b7280' }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#6b7280' }}
              tickFormatter={formatCurrency}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{ fontSize: '12px', paddingTop: '20px' }}
              iconType="rect"
            />
            <Bar dataKey="energy" name="Energy" stackId="cost" fill="#f59e0b" radius={[0, 0, 0, 0]} />
            <Bar dataKey="standing" name="Standing Charge" stackId="cost" fill="#94a3b8" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  )
}
